import type {
  CreateScenarioAdjustmentRequestApi,
  ScenarioAdjustmentResponseApi,
  ScenarioAdjustmentViewNode,
  UpdateScenarioAdjustmentRequestApi,
} from "../models/scenarioAdjustment";

export type AdjustmentType =
  | "percentage_adj"
  | "transaction_adj"
  | "new_transaction";
export type AdjustmentDirection = "inflow" | "outflow";
export type AdjustmentFrequency =
  | "one-off"
  | "daily"
  | "weekly"
  | "monthly"
  | "quarterly"
  | "annually";

export interface ScenarioAdjustment {
  id: number;
  scenarioId: number;
  adjustmentType: AdjustmentType;
  adjustmentDirection: AdjustmentDirection;
  startDate: string;
  endDate: string | null;
  frequency: AdjustmentFrequency;
  confidenceLevel: number;
  description: string | null;
  additionalComments: string | null;
  adjustmentValuePercentage: number | null;
  adjustedValue: number | null;
  adjustedDueDate: string | null;
  linkedTransactionId: number | null;
  transactionType: string | null;
  monthEndHandling: number;
  netCashImpact: number | null;
  linkedAccountName: string | null;
  linkedTransactionSource: string | null;
  linkedTransactionAmount: number | null;
  linkedTransactionDueDate: string | null;
  linkedTransactionStatus: string | null;
  createdByUserId: string;
  createdByUserName: string;
  dateCreated: string;
  dateModified: string;
}

/**
 * Map a REST API ScenarioAdjustmentResponse to a frontend ScenarioAdjustment.
 */
export function mapAdjustmentFromApi(
  response: ScenarioAdjustmentResponseApi,
): ScenarioAdjustment {
  return {
    id: response.id,
    scenarioId: response.scenarioId,
    adjustmentType: response.adjustmentType as AdjustmentType,
    adjustmentDirection: response.adjustmentDirection as AdjustmentDirection,
    startDate: response.startDate,
    endDate: response.endDate,
    frequency: response.frequency as AdjustmentFrequency,
    confidenceLevel: response.confidenceLevel,
    description: response.description,
    additionalComments: response.additionalComments,
    adjustmentValuePercentage: response.adjustmentValuePercentage,
    adjustedValue: response.adjustedValue,
    adjustedDueDate: response.adjustedDueDate,
    linkedTransactionId: response.linkedTransactionId,
    transactionType: response.transactionType,
    monthEndHandling: response.monthEndHandling,
    netCashImpact: null,
    linkedAccountName: null,
    linkedTransactionSource: null,
    linkedTransactionAmount: null,
    linkedTransactionDueDate: null,
    linkedTransactionStatus: null,
    createdByUserId: response.createdByUserId,
    createdByUserName: response.createdByUserName,
    dateCreated: response.dateCreated,
    dateModified: response.dateModified,
  };
}

/**
 * Map a GraphQL ScenarioAdjustmentViewNode to a frontend ScenarioAdjustment.
 */
export function mapAdjustmentFromGraphql(
  node: ScenarioAdjustmentViewNode,
): ScenarioAdjustment {
  return {
    id: Number(node.id),
    scenarioId: node.scenarioId,
    adjustmentType: node.adjustmentType as AdjustmentType,
    adjustmentDirection: node.adjustmentDirection as AdjustmentDirection,
    startDate: node.startDate,
    endDate: node.endDate,
    frequency: node.frequency as AdjustmentFrequency,
    confidenceLevel: node.confidenceLevel,
    description: node.description,
    additionalComments: node.additionalComments,
    adjustmentValuePercentage: node.adjustmentValuePercentage,
    adjustedValue: node.adjustedValue,
    adjustedDueDate: node.adjustedDueDate,
    linkedTransactionId: node.linkedTransactionId,
    transactionType: node.transactionType,
    monthEndHandling: node.monthEndHandling,
    netCashImpact: node.netCashImpact,
    linkedAccountName: node.linkedAccountName,
    linkedTransactionSource: node.linkedTransactionSource,
    linkedTransactionAmount: node.linkedTransactionAmount,
    linkedTransactionDueDate: node.linkedTransactionDueDate,
    linkedTransactionStatus: node.linkedTransactionStatus,
    createdByUserId: node.createdByUserId,
    createdByUserName: node.createdByUserName,
    dateCreated: node.dateCreated,
    dateModified: node.dateModified,
  };
}

/**
 * Convert builder form data into a CreateScenarioAdjustmentRequest for the API.
 */
export function toCreateAdjustmentRequest(
  direction: AdjustmentDirection,
  adjustmentType: AdjustmentType,
  data: {
    startDate: Date | string;
    endDate?: Date | string | null;
    frequency?: string;
    confidenceLevel?: number;
    description?: string | null;
    additionalComments?: string | null;
    adjustmentValuePercentage?: number | null;
    adjustedValue?: number | null;
    adjustedDueDate?: Date | string | null;
    linkedTransactionId?: number | null;
    transactionType?: string | null;
    monthEndHandling?: number;
  },
): CreateScenarioAdjustmentRequestApi {
  return {
    adjustmentType,
    adjustmentDirection: direction,
    startDate: toDateString(data.startDate),
    endDate: data.endDate ? toDateString(data.endDate) : null,
    frequency: data.frequency ?? "one-off",
    confidenceLevel: data.confidenceLevel ?? 75,
    description: data.description ?? null,
    additionalComments: data.additionalComments ?? null,
    adjustmentValuePercentage: data.adjustmentValuePercentage ?? null,
    adjustedValue: data.adjustedValue ?? null,
    adjustedDueDate: data.adjustedDueDate
      ? toDateString(data.adjustedDueDate)
      : null,
    linkedTransactionId: data.linkedTransactionId ?? null,
    transactionType: data.transactionType ?? null,
    monthEndHandling: data.monthEndHandling ?? 0,
  };
}

/**
 * Convert partial updates into an UpdateScenarioAdjustmentRequest for the API.
 */
export function toUpdateAdjustmentRequest(
  updates: Partial<{
    adjustmentType: string;
    adjustmentDirection: string;
    startDate: Date | string;
    endDate: Date | string | null;
    frequency: string;
    confidenceLevel: number;
    description: string | null;
    additionalComments: string | null;
    adjustmentValuePercentage: number | null;
    adjustedValue: number | null;
    adjustedDueDate: Date | string | null;
    linkedTransactionId: number | null;
    transactionType: string | null;
    monthEndHandling: number;
  }>,
): UpdateScenarioAdjustmentRequestApi {
  const request: UpdateScenarioAdjustmentRequestApi = {};

  if (updates.adjustmentType !== undefined)
    request.adjustmentType = updates.adjustmentType;
  if (updates.adjustmentDirection !== undefined)
    request.adjustmentDirection = updates.adjustmentDirection;
  if (updates.startDate !== undefined)
    request.startDate = toDateString(updates.startDate);
  if (updates.endDate !== undefined)
    request.endDate = updates.endDate ? toDateString(updates.endDate) : null;
  if (updates.frequency !== undefined) request.frequency = updates.frequency;
  if (updates.confidenceLevel !== undefined)
    request.confidenceLevel = updates.confidenceLevel;
  if (updates.description !== undefined)
    request.description = updates.description;
  if (updates.additionalComments !== undefined)
    request.additionalComments = updates.additionalComments;
  if (updates.adjustmentValuePercentage !== undefined)
    request.adjustmentValuePercentage = updates.adjustmentValuePercentage;
  if (updates.adjustedValue !== undefined)
    request.adjustedValue = updates.adjustedValue;
  if (updates.adjustedDueDate !== undefined)
    request.adjustedDueDate = updates.adjustedDueDate
      ? toDateString(updates.adjustedDueDate)
      : null;
  if (updates.linkedTransactionId !== undefined)
    request.linkedTransactionId = updates.linkedTransactionId;
  if (updates.transactionType !== undefined)
    request.transactionType = updates.transactionType;
  if (updates.monthEndHandling !== undefined)
    request.monthEndHandling = updates.monthEndHandling;

  return request;
}

function parseDateLocal(value: string | Date): Date {
  if (value instanceof Date) return value;
  const parts = value.split("T")[0].split("-");
  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
}

function toDateString(value: Date | string): string {
  if (value instanceof Date) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return value;
}

// ─── Confidence mapping ──────────────────────────────────────────────────────

const CONFIDENCE_STRING_TO_INT: Record<string, number> = {
  "almost certain": 100,
  probable: 75,
  possible: 50,
  unlikely: 25,
};

const CONFIDENCE_INT_TO_STRING: Record<number, string> = {
  100: "almost certain",
  75: "probable",
  50: "possible",
  25: "unlikely",
};

function confidenceToInt(value: string | number | undefined): number {
  if (typeof value === "number") return value;
  if (typeof value === "string")
    return CONFIDENCE_STRING_TO_INT[value.toLowerCase()] ?? 75;
  return 75;
}

function confidenceToString(value: number): string {
  return CONFIDENCE_INT_TO_STRING[value] ?? "probable";
}

// ─── Builder form data ↔ API format conversion ──────────────────────────────

/**
 * Convert a builder form submission into a CreateScenarioAdjustmentRequest.
 * The form `type` field tells us which form was used; `direction` is passed
 * explicitly by the save handler (revenue → inflow, cost → outflow).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function builderFormToCreateRequest(
  formData: any,
  direction: AdjustmentDirection,
): CreateScenarioAdjustmentRequestApi {
  const now = new Date().toISOString().split("T")[0];

  switch (formData.type) {
    case "adjust-revenue-percent":
    case "adjust-costs-percent":
      return {
        adjustmentType: "percentage_adj",
        adjustmentDirection: direction,
        startDate: formData.effectiveDate
          ? toDateString(formData.effectiveDate)
          : now,
        // One-off has no window — never send an end date for it.
        endDate:
          formData.frequency === "one-off"
            ? null
            : formData.endDate
              ? toDateString(formData.endDate)
              : null,
        frequency: formData.frequency ?? "monthly",
        confidenceLevel: confidenceToInt(formData.confidence),
        description: formData.reason || formData.name || null,
        additionalComments: null,
        adjustmentValuePercentage: formData.adjustmentPercent ?? null,
        adjustedValue: null,
        adjustedDueDate: null,
        linkedTransactionId: null,
        transactionType: formData.category || null,
        monthEndHandling: formData.monthlyDateMode === "last-day" ? 1 : 0,
      };

    case "adjust-transaction":
      return {
        adjustmentType: "transaction_adj",
        adjustmentDirection: direction,
        startDate: formData.newDueDate
          ? toDateString(formData.newDueDate)
          : formData.originalDueDate
            ? toDateString(formData.originalDueDate)
            : now,
        endDate: null,
        frequency: "one-off",
        confidenceLevel: confidenceToInt(formData.confidence),
        description:
          formData.reason ||
          formData.name ||
          `Adjust ${formData.customer || formData.supplier || "transaction"}`,
        additionalComments: null,
        adjustmentValuePercentage: null,
        adjustedValue:
          formData.adjustedValue ?? formData.originalAmount ?? null,
        adjustedDueDate: formData.newDueDate
          ? toDateString(formData.newDueDate)
          : null,
        linkedTransactionId: formData.transactionId
          ? Number(formData.transactionId)
          : null,
        transactionType: null,
        monthEndHandling: formData.monthlyDateMode === "last-day" ? 1 : 0,
      };

    case "add-scenario-transaction": {
      const isCost = formData.businessContext !== undefined;
      const txDate = formData.date ?? formData.expectedDate ?? new Date();
      return {
        adjustmentType: "new_transaction",
        adjustmentDirection: direction,
        startDate: toDateString(txDate),
        endDate: formData.endDate ? toDateString(formData.endDate) : null,
        frequency: formData.recurrence ?? formData.frequency ?? "one-off",
        confidenceLevel: confidenceToInt(formData.confidence),
        description: formData.description || formData.name || "New transaction",
        additionalComments: isCost
          ? formData.businessContext || null
          : formData.reason || null,
        adjustmentValuePercentage: null,
        adjustedValue: formData.amount ?? null,
        adjustedDueDate: null,
        linkedTransactionId: null,
        transactionType: formData.transactionType ?? null,
        monthEndHandling: formData.monthlyDateMode === "last-day" ? 1 : 0,
      };
    }

    default:
      return {
        adjustmentType: "new_transaction",
        adjustmentDirection: direction,
        startDate: formData.effectiveDate
          ? toDateString(formData.effectiveDate)
          : formData.date
            ? toDateString(formData.date)
            : now,
        endDate: formData.endDate ? toDateString(formData.endDate) : null,
        frequency: formData.frequency ?? "one-off",
        confidenceLevel: confidenceToInt(formData.confidence),
        description: formData.description || formData.name || null,
        additionalComments: formData.reason || null,
        adjustmentValuePercentage: null,
        adjustedValue: formData.amount ?? null,
        adjustedDueDate: null,
        linkedTransactionId: null,
        transactionType: formData.transactionType ?? null,
        monthEndHandling: formData.monthlyDateMode === "last-day" ? 1 : 0,
      };
  }
}

/**
 * Convert an API ScenarioAdjustmentResponse into the builder's internal format
 * so existing AdjustmentCard rendering and edit forms work correctly.
 */
export function apiResponseToBuilderFormat(
  adj: ScenarioAdjustmentResponseApi &
    Partial<
      Pick<
        ScenarioAdjustmentViewNode,
        | "linkedAccountName"
        | "linkedTransactionSource"
        | "linkedTransactionAmount"
        | "linkedTransactionDueDate"
        | "linkedTransactionStatus"
      >
    >,
  netCashImpact?: number | null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
  const isRevenue = adj.adjustmentDirection === "inflow";

  switch (adj.adjustmentType) {
    case "percentage_adj": {
      const canonicalType = isRevenue ? "change-revenue" : "change-costs";
      const percent = adj.adjustmentValuePercentage ?? 0;
      const frequency = adj.frequency ?? "monthly";
      const isOneOff = frequency === "one-off";
      // Percentage adjustments store no pre-computed amount (adjustedValue is
      // null); the real money figure is the engine-derived net cash impact.
      // The builder form itself re-derives per-period figures from the
      // percentage + live base forecast, so we only surface the window total
      // here for display and let the form recompute the rest.
      const windowTotal = netCashImpact ?? 0;
      const monthlyValue = windowTotal / 3; // ~3 months over the 90-day window

      return {
        id: adj.id,
        type: canonicalType,
        name: `Adjust ${isRevenue ? "revenue" : "costs"} ${percent >= 0 ? "+" : ""}${percent}%`,
        description: adj.description ?? "",
        amount: windowTotal,
        adjustmentAmount: windowTotal,
        netCashImpact: netCashImpact ?? null,
        date: parseDateLocal(adj.startDate),
        effectiveDate: parseDateLocal(adj.startDate),
        // One-off adjustments have no end date; never fabricate one.
        endDate:
          !isOneOff && adj.endDate ? parseDateLocal(adj.endDate) : undefined,
        frequency,
        monthEndHandling: adj.monthEndHandling ?? 0,
        adjustmentPercent: percent,
        reason: adj.description ?? "",
        confidence: confidenceToString(adj.confidenceLevel),
        monthlyDateMode: adj.monthEndHandling === 1 ? "last-day" : "same-date",
        category: adj.transactionType ?? "",
        details: isRevenue
          ? {
              monthlyAmount: monthlyValue,
              percentage: percent,
              frequency,
              monthlyDateMode:
                adj.monthEndHandling === 1 ? "last-day" : "same-date",
              confidence: confidenceToString(adj.confidenceLevel),
            }
          : {
              ...(percent < 0
                ? { monthlySavings: Math.abs(monthlyValue) }
                : { monthlyIncrease: Math.abs(monthlyValue) }),
              changeType: percent < 0 ? "reduction" : "increase",
              percentage: percent,
              frequency,
              monthlyDateMode:
                adj.monthEndHandling === 1 ? "last-day" : "same-date",
              confidence: confidenceToString(adj.confidenceLevel),
            },
        createdAt: new Date(adj.dateCreated),
        createdBy: adj.createdByUserName,
      };
    }

    case "transaction_adj": {
      const canonicalType = isRevenue ? "adjust-revenue" : "adjust-cost";
      // The view joins the linked transaction, so its account, amount and
      // original due date are available here — surface them so the edit
      // form can display the transaction without a separate lookup.
      const counterparty = adj.linkedAccountName ?? undefined;
      return {
        id: adj.id,
        type: canonicalType,
        name: adj.description ?? "Adjust transaction",
        description: adj.description ?? "",
        amount: adj.adjustedValue ?? 0,
        netCashImpact: netCashImpact ?? null,
        date: adj.adjustedDueDate
          ? parseDateLocal(adj.adjustedDueDate)
          : parseDateLocal(adj.startDate),
        newDueDate: adj.adjustedDueDate
          ? parseDateLocal(adj.adjustedDueDate)
          : undefined,
        originalDueDate: adj.linkedTransactionDueDate
          ? parseDateLocal(adj.linkedTransactionDueDate)
          : undefined,
        originalAmount: adj.linkedTransactionAmount ?? adj.adjustedValue ?? 0,
        customer: isRevenue ? counterparty : undefined,
        supplier: isRevenue ? undefined : counterparty,
        transactionType: adj.transactionType ?? undefined,
        linkedTransactionStatus: adj.linkedTransactionStatus ?? undefined,
        frequency: adj.frequency ?? "one-off",
        details: {
          transactionId: adj.linkedTransactionId
            ? String(adj.linkedTransactionId)
            : undefined,
          confidence: confidenceToString(adj.confidenceLevel),
        },
        createdAt: new Date(adj.dateCreated),
        createdBy: adj.createdByUserName,
      };
    }

    case "new_transaction": {
      const canonicalType = isRevenue ? "add-revenue" : "add-cost";
      return {
        id: adj.id,
        type: canonicalType,
        name: adj.description ?? "New transaction",
        description: adj.additionalComments ?? "",
        amount: adj.adjustedValue ?? 0,
        netCashImpact: netCashImpact ?? null,
        date: parseDateLocal(adj.startDate),
        endDate: adj.endDate ? parseDateLocal(adj.endDate) : undefined,
        frequency: adj.frequency ?? "one-off",
        recurrence: adj.frequency ?? "one-off",
        confidence: confidenceToString(adj.confidenceLevel),
        monthEndHandling: adj.monthEndHandling ?? 0,
        monthlyDateMode: adj.monthEndHandling === 1 ? "last-day" : "same-date",
        transactionType: adj.transactionType,
        details: {
          monthlyAmount: adj.adjustedValue ?? 0,
          transactionType: adj.transactionType,
          confidence: confidenceToString(adj.confidenceLevel),
          frequency: adj.frequency ?? "one-off",
          monthlyDateMode:
            adj.monthEndHandling === 1 ? "last-day" : "same-date",
        },
        createdAt: new Date(adj.dateCreated),
        createdBy: adj.createdByUserName,
      };
    }

    default:
      return {
        id: adj.id,
        type: isRevenue ? "add-revenue" : "add-cost",
        name: adj.description ?? "Adjustment",
        description: adj.description ?? "",
        amount: adj.adjustedValue ?? 0,
        netCashImpact: netCashImpact ?? null,
        date: parseDateLocal(adj.startDate),
        frequency: adj.frequency ?? "one-off",
        details: {
          confidence: confidenceToString(adj.confidenceLevel),
        },
        createdAt: new Date(adj.dateCreated),
        createdBy: adj.createdByUserName,
      };
  }
}
